import { type Camera, Vector3 } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { CameraMode, CameraInitOptions, TunableSpec } from './cameraMode';

// The "sit in the chair" camera: SEATED look-around — anchored in the seat, you
// turn your head to look around the room (John, 2026-06-29: "anchored in the chair
// and the mouse/touch moves it around but anchored to chair — like a simulation of
// sitting in a chair looking around").
//
// It's an OrbitControls (constrained: pan off, a clamped look cone, a subtle lean
// band) but with the pivot collapsed to a point JUST IN FRONT of the eye
// (`pivotRadius`, ~0.18 m — a head's length) instead of the distant table. That's
// the whole trick: orbiting a NEAR pivot reads as turning your head from a fixed
// seat, where orbiting the far table (the old behavior) read as circling the table.
// The eye still sways a little at the extremes (the natural lean of looking over
// your shoulder); a smaller pivotRadius pins it tighter. Reusing OrbitControls
// keeps the cloth-grab coexistence for free — a grab mutes the controls, so
// dragging the cloth while seated deforms it instead of swinging the view.
//
// goToBookmark('chair'/'chairnew') hands off here (seeded with the chair bookmark's
// target = the table) so the initial gaze faces the table; from there you look
// around. The look-cone width, the lean band, the pivot, and the standpoint are
// framing — John's eye (dev-panel tunables, flagged).
const TUNABLES: TunableSpec[] = [
  { key: 'yawRange', label: 'seat yaw (rad)', min: 0.4, max: 2.6, step: 0.05 },
  { key: 'pitchRange', label: 'seat pitch (rad)', min: 0.05, max: 0.9, step: 0.02 },
  { key: 'pivotRadius', label: 'head pivot (m)', min: 0.08, max: 1.2, step: 0.02 },
  { key: 'zoomBand', label: 'lean (zoom ±frac)', min: 0, max: 0.6, step: 0.02 },
  { key: 'rotateSpeed', label: 'rotate speed', min: 0.1, max: 2, step: 0.1 },
];

export class LockedFreeformMode implements CameraMode {
  private controls: OrbitControls | null = null;
  // The orbit angles the cone centres on — captured at init from the seated
  // framing, so the look-around is symmetric about where the chair faces.
  private centerAz = 0;
  private centerPol = Math.PI / 2;
  // The locked eye↔pivot distance — equals pivotRadius (the head-turn radius),
  // NOT the distance to the table. The lean band rides this.
  private seatDistance = 0.18;
  private fwd = new Vector3(); // scratch: the seated forward, eye → pivot

  private _yawRange = 1.4;     // ± rad ≈ ±80° — look AROUND the room, not just the table
  private _pitchRange = 0.34;  // ± rad ≈ ±19° up/down — modest
  private _pivotRadius = 0.18; // eye → pivot (m): a head's length, so it's a head-turn
  private _zoomBand = 0.12;    // ±12% dolly on the (tiny) pivot radius — a faint lean
  private _rotateSpeed = 0.8;

  get yawRange(): number { return this._yawRange; }
  set yawRange(v: number) { this._yawRange = v; this.applyLimits(); }
  get pitchRange(): number { return this._pitchRange; }
  set pitchRange(v: number) { this._pitchRange = v; this.applyLimits(); }
  get pivotRadius(): number { return this._pivotRadius; }
  set pivotRadius(v: number) { this._pivotRadius = v; this.applyPivot(); }
  get zoomBand(): number { return this._zoomBand; }
  set zoomBand(v: number) { this._zoomBand = v; this.applyLimits(); }
  get rotateSpeed(): number { return this._rotateSpeed; }
  set rotateSpeed(v: number) { this._rotateSpeed = v; if (this.controls) this.controls.rotateSpeed = v; }

  constructor(
    private camera: Camera,
    private domElement: HTMLElement,
    /** Pivot fallback if no entry target is seeded (normally the seat faces the
     *  table via the chair bookmark's target, passed through init()). */
    private fallbackTarget: Vector3,
  ) {}

  init(options?: CameraInitOptions): void {
    const c = new OrbitControls(this.camera, this.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.rotateSpeed = this._rotateSpeed;
    c.enablePan = false; // seated — you don't slide the chair around
    c.enableZoom = true; // you CAN lean in/out a hair (the band in applyLimits)
    // Seed the target with what the seat faces (the chair bookmark's table target,
    // from the tween hand-off; else the fallback) so the seated FORWARD is correct.
    c.target.copy(options?.target ?? this.fallbackTarget);
    c.update();
    this.controls = c;
    // Collapse the far pivot to a near head-turn pivot in front of the eye, then
    // clamp the cone around the resulting seated facing.
    this.applyPivot();
  }

  /** Move the orbit pivot to a point `pivotRadius` in front of the eye along the
   *  seated forward, keeping the eye where it is, and re-lock the distance. So
   *  orbiting turns the head rather than circling the room. Re-centres the look
   *  cone on the current gaze (init, and any live pivotRadius tweak). */
  private applyPivot(): void {
    const c = this.controls;
    if (!c) return;
    // Seated forward = eye → current pivot. (After init's seed this points at the
    // table; on a live tweak it's wherever you're currently looking.)
    this.fwd.subVectors(c.target, this.camera.position);
    if (this.fwd.lengthSq() < 1e-8) this.fwd.set(0, 0, -1); // degenerate seed → look ahead
    this.fwd.normalize();
    c.target.copy(this.camera.position).addScaledVector(this.fwd, this._pivotRadius);
    this.seatDistance = this._pivotRadius;
    c.update();
    this.centerAz = c.getAzimuthalAngle();
    this.centerPol = c.getPolarAngle();
    this.applyLimits();
  }

  /** Re-clamp the look cone + lean band around the captured seated centre. */
  private applyLimits(): void {
    const c = this.controls;
    if (!c) return;
    c.minAzimuthAngle = this.centerAz - this._yawRange;
    c.maxAzimuthAngle = this.centerAz + this._yawRange;
    c.minPolarAngle = Math.max(0.01, this.centerPol - this._pitchRange);
    c.maxPolarAngle = Math.min(Math.PI - 0.01, this.centerPol + this._pitchRange);
    // A faint dolly band around the (small) seat pivot — a lean, not a fly.
    c.minDistance = this.seatDistance * (1 - this._zoomBand);
    c.maxDistance = this.seatDistance * (1 + this._zoomBand);
    c.update();
  }

  update(): void {
    this.controls?.update();
  }

  dispose(): void {
    this.controls?.dispose();
    this.controls = null;
  }

  getTunables() {
    return { target: this, specs: TUNABLES };
  }
}
